
import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEllipsisVertical,
  faMagnifyingGlass,
  faClock,
  faCalendarDays,
} from "@fortawesome/free-solid-svg-icons";
import { Dropdown } from "react-bootstrap";
import ReactQuill from "react-quill";
import { FaStickyNote } from "react-icons/fa";
import { toast } from "sonner";
import { UnauthViewModal } from "../../CommonComponents/UnauthViewModal";
import DeleteModal from "../DeleteModal";
import {
  DeleteRequest,
  GetRequest,
  PostRequest,
  PutRequest,
} from "../../Utils/hooks/ApiHandler";
import { useGetEssentials } from "../../Utils/hooks/useGetEssentials";
import {
  formatDateInAlphabeticalMonth,
  formatTime,
} from "../../Utils/helpers/commonhelper";

/**
 * ProjectNotes
 * -------------------------------------------------------------------------
 * Standalone Notes tab for a project, rebuilt as a two-pane workspace
 * (list on the left, full note detail on the right) to match the visual
 * language of the Meetings tab. Same data contract & business logic as
 * before - fetch / create / update / delete notes - just a new layout.
 * By default the top-most (latest) note is selected in the detail panel.
 * -------------------------------------------------------------------------
 */

const stripHtml = (html) =>
  (html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const truncate = (text, limit = 90) =>
  text && text.length > limit ? text.substring(0, limit) + "…" : text;

const isSameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const initialsOf = (name) =>
  name
    ?.split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase() || "?";

const styles = {
  listButtonsStyles: {
    fontSize: "13px",
    height: "34px",
    width: "auto",
    gap: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
    color: "#fff",
  },
};

export const ProjectNotes = ({
  projectData,
  headers,
  extras,
  customerId,
  customer,
  noteGrant,
}) => {
  const { loginUserId } = useGetEssentials();
  const [notesError, setNotesError] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesContent, setNotesContent] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [isNotesLoading, setIsNotesLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNoteId, setSelectedNoteId] = useState(null);

  // scroll back to the editor when it opens
  const notesRef = useRef(null);
  useEffect(() => {
    if (isNotesOpen && notesRef.current) {
      notesRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [isNotesOpen]);

  useEffect(() => {
    if (projectData?.id) {
      fetchNotes(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, projectData?.id]);

  const fetchNotes = async (isInitial = false) => {
    if (isInitial) setIsInitialLoading(true);
    setIsNotesLoading(true);
    setNotesError(null);

    try {
      const response = await GetRequest(
        `project/fetchNotes/${projectData?.id}/${customerId}/${loginUserId}`,
        { headers },
        extras,
      );

      if (response.data) {
        setNotes(response.data);
      } else {
        setNotesError("Failed to load notes");
      }
    } catch (error) {
      setNotesError(error.message || "Error loading notes");
    } finally {
      setIsNotesLoading(false);
      if (isInitial) setIsInitialLoading(false);
    }
  };

  // ---------------- search + default (top-most) selection ----------------
  const filteredNotes = useMemo(() => {
    if (!searchTerm) return notes;
    const term = searchTerm.toLowerCase();
    return notes.filter((n) => stripHtml(n.notes).toLowerCase().includes(term));
  }, [notes, searchTerm]);

  useEffect(() => {
    if (!filteredNotes || filteredNotes.length === 0) {
      if (selectedNoteId !== null) setSelectedNoteId(null);
      return;
    }
    if (!filteredNotes.some((n) => n.id === selectedNoteId)) {
      setSelectedNoteId(filteredNotes[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredNotes]);

  const selectedNote = notes.find((n) => n.id === selectedNoteId) || null;

  // Handle create/update note
  const handleSaveNote = async () => {
    if (!notesContent.trim()) {
      setNotesError("Please enter some notes before submitting");
      return;
    }

    setIsNotesLoading(true);
    setNotesError(null);
    const savingNoteId = editingNoteId;
    try {
      let response;
      const noteData = {
        customer_id: customerId,
        project: projectData?.id,
        notes: notesContent,
        user_login_id: loginUserId,
      };

      if (editingNoteId) {
        response = await PutRequest(
          "project/updateNote",
          { ...noteData, note_id: editingNoteId },
          { headers },
          extras,
        );
      } else {
        response = await PostRequest(
          "project/createNote",
          noteData,
          false,
          { headers },
          extras,
        );
      }

      if (response.data) {
        toast.success(
          `Note ${editingNoteId ? "updated" : "created"} successfully`,
        );
        setNotesContent("");
        setEditingNoteId(null);
        setIsNotesOpen(false);
        // keep the same note selected after an edit; let a new note default to the top
        setSelectedNoteId(savingNoteId || null);
        fetchNotes();
      } else {
        setNotesError(
          response.message ||
            `Failed to ${editingNoteId ? "update" : "create"} note`,
        );
      }
    } catch (error) {
      setNotesError(error.message || "Error saving note");
    } finally {
      setIsNotesLoading(false);
    }
  };

  const handleEditNote = async (noteId) => {
    setIsNotesLoading(true);
    setNotesError(null);

    try {
      const response = await GetRequest(
        `project/getNotesById/${noteId}/${projectData?.id}/${customerId}/${loginUserId}`,
        { headers },
        extras,
      );

      if (response.data && response.data.length > 0) {
        const note = response.data[0];
        setNotesContent(note.notes);
        setEditingNoteId(noteId);
        setIsNotesOpen(true);
      } else {
        setNotesError("Failed to load note for editing");
      }
    } catch (error) {
      setNotesError(error.message || "Error loading note");
    } finally {
      setIsNotesLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    setIsNotesLoading(true);
    setNotesError(null);
    try {
      const response = await DeleteRequest(
        `project/deleteNotes/${projectData?.id}/${noteId.id}/${loginUserId}`,
        { headers },
        extras,
      );

      if (response.status == "success") {
        toast.success("Note deleted successfully");
        fetchNotes();
        setShowDeleteModal(false);
      } else {
        setNotesError(response.message || "Failed to delete note");
      }
    } catch (error) {
      setNotesError(error.message || "Error deleting note");
    } finally {
      setIsNotesLoading(false);
    }
  };

  const openCreateNote = () => {
    if (!noteGrant?.isCreate) {
      toast.warning("You are not authorized for this action!");
      return;
    }
    setNotesContent("");
    setEditingNoteId(null);
    setNotesError(null);
    setIsNotesOpen(true);
  };

  const closeEditor = () => {
    setEditingNoteId(null);
    setIsNotesOpen(false);
    setNotesContent("");
    setNotesError(null);
  };

  const now = new Date();

  return (
    <div className="pnc-page">
      <style>{`
        .pnc-page{ --pnc-primary:#7a3a5e; --pnc-primary-soft:rgba(122,58,94,0.08); --pnc-border:#e7e8f1; --pnc-bg:#f7f8fc; --pnc-text:#20223a; --pnc-muted:#767892; background:var(--pnc-bg); border-radius:12px; height:calc(100vh - 8rem); max-height:calc(100vh - 8rem); display:flex; flex-direction:column; font-family:inherit; overflow:hidden; }
        .pnc-topbar{ display:flex; align-items:center; justify-content:space-between; gap:16px; padding:20px 24px 14px; flex-wrap:wrap; }
        .pnc-title{ font-size:22px; font-weight:600; color:var(--pnc-text); margin:0; }
        .pnc-subtitle{ font-size:13px; color:var(--pnc-muted); margin-top:2px; }
        .pnc-topbar-actions{ display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
        .pnc-search{ position:relative; }
        .pnc-search input{ border:1px solid var(--pnc-border); border-radius:8px; padding:8px 12px 8px 34px; font-size:13px; min-width:220px; background:#fff; }
        .pnc-search input:focus{ outline:none; border-color:var(--pnc-primary); box-shadow:0 0 0 3px var(--pnc-primary-soft); }
        .pnc-search svg{ position:absolute; left:11px; top:50%; transform:translateY(-50%); color:var(--pnc-muted); font-size:13px; }
        .pnc-btn-primary{ background:var(--pnc-primary); border:none; color:#fff; font-size:13px; font-weight:500; height:36px; padding:0 16px; border-radius:8px; display:flex; align-items:center; gap:8px; white-space:nowrap; }
        .pnc-btn-primary:hover{ background:#602c49; color:#fff; }
        .pnc-btn-primary:disabled{ opacity:.55; }
        .pnc-body{ display:flex; gap:7px; padding:0 24px 24px; flex:1; min-height:0; overflow:hidden; }
        .pnc-card{ background:#fff; border:1px solid var(--pnc-border); border-radius:12px; }

        /* ---- list panel (left) ---- */
        .pnc-list-panel{ width:360px; flex:none; display:flex; flex-direction:column; min-height:0; }
        .pnc-list-card{ display:flex; flex-direction:column; min-height:0; flex:1; padding:14px; overflow:hidden; }
        .pnc-list-card-head{ display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; flex-shrink:0; }
        .pnc-list-card-head h6{ font-size:14px; font-weight:600; margin:0; color:var(--pnc-text); display:flex; align-items:center; gap:8px; }
        .pnc-count-pill{ font-size:11px; font-weight:700; color:var(--pnc-primary); background:var(--pnc-primary-soft); border-radius:20px; padding:2px 9px; }
        .pnc-list{ display:flex; flex-direction:column; gap:10px; flex:1; min-height:0; padding-right:2px;
    overflow-y: auto;
    scroll-behavior: smooth;
    scrollbar-width: thin; }
        .pnc-list::-webkit-scrollbar{ width:5px; }
        .pnc-list::-webkit-scrollbar-thumb{ background:#e2e8f0; border-radius:10px; }
        .pnc-item{ display:flex; align-items:flex-start; gap:10px; padding:12px 14px; border-radius:12px; border:1px solid var(--pnc-border); background:#fff; cursor:pointer; box-shadow:0 1px 2px rgba(20,20,50,0.03); transition:box-shadow .15s ease, border-color .15s ease; }
        .pnc-item:hover{ border-color:var(--pnc-primary); box-shadow:0 4px 14px rgba(20,20,50,0.08); }
        .pnc-item.active{ background:var(--pnc-primary-soft); border-color:var(--pnc-primary); box-shadow:0 4px 14px rgba(122,58,94,0.12); }
        .pnc-item-avatar{ width:32px; height:32px; border-radius:50%; flex-shrink:0; display:flex; align-items:center; justify-content:center; color:#fff; font-weight:700; font-size:11.5px; background:linear-gradient(45deg, var(--pnc-primary), #9d4d7a); box-shadow:0 1px 3px rgba(20,20,50,.15); }
        .pnc-item-content{ min-width:0; flex:1; }
        .pnc-item-snippet{ font-size:13.5px; font-weight:600; color:var(--pnc-text); line-height:1.4; margin-bottom:6px; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
        .pnc-item-meta{ display:flex; align-items:center; gap:6px; font-size:11.5px; color:var(--pnc-muted); font-weight:500; }
        .pnc-item-meta .dot{ width:3px; height:3px; border-radius:50%; background:var(--pnc-muted); flex-shrink:0; }
        .pnc-list-empty{ font-size:12.5px; color:var(--pnc-muted); text-align:center; padding:24px 8px; }

        /* ---- detail panel (right) ---- */
        .pnc-detail-panel{ flex:1; min-width:0; display:flex; flex-direction:column; min-height:0; }
        .pnc-detail-card{ background:#fff; border:1px solid var(--pnc-border); border-radius:12px; flex:1; display:flex; flex-direction:column; min-height:0; overflow:hidden; position:relative; }
        .pnc-detail-header{ display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid var(--pnc-border); flex-shrink:0; }
        .pnc-detail-header h6{ font-size:14px; font-weight:600; margin:0; color:var(--pnc-text); }
        .pnc-detail-body{ padding:22px 24px; overflow-y:auto; flex:1; min-height:0; scroll-behavior:smooth; scrollbar-width:thin; }
        .pnc-detail-body::-webkit-scrollbar, .pnc-editor-wrap::-webkit-scrollbar{ width:5px; }
        .pnc-detail-body::-webkit-scrollbar-thumb, .pnc-editor-wrap::-webkit-scrollbar-thumb{ background:#e2e8f0; border-radius:10px; }
        .pnc-detail-top{ display:flex; align-items:center; gap:12px; margin-bottom:18px; }
        .pnc-detail-avatar{ width:42px; height:42px; border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff; font-weight:700; font-size:14px; background:linear-gradient(45deg, var(--pnc-primary), #9d4d7a); flex-shrink:0; }
        .pnc-detail-name{ font-weight:700; font-size:14.5px; color:var(--pnc-text); }
        .pnc-detail-date{ display:flex; align-items:center; gap:6px; font-size:12.5px; color:var(--pnc-muted); font-weight:500; margin-top:2px; }
        .pnc-status-pill{ margin-left:auto; font-size:11px; font-weight:700; padding:4px 12px; border-radius:20px; text-transform:capitalize; white-space:nowrap; }
        .pnc-detail-content{ background:var(--pnc-bg); border-left:3px solid var(--pnc-primary); border-radius:10px; padding:16px 18px; font-size:14px; color:#374151; line-height:1.7; }
        .pnc-detail-content p{ margin:0 0 10px; }
        .pnc-detail-content p:last-child{ margin-bottom:0; }
        .pnc-detail-updated{ margin-top:14px; font-size:12px; color:var(--pnc-muted); font-style:italic; }
        .pnc-detail-empty{ flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px; color:var(--pnc-muted); }
        .pnc-detail-empty-icon{ width:56px; height:56px; border-radius:50%; background:var(--pnc-primary-soft); color:var(--pnc-primary); display:flex; align-items:center; justify-content:center; font-size:22px; }
        .pnc-no-arrow::after{ display:none !important; }
        .pnc-card-actions button{ background:transparent; border:none; color:var(--pnc-muted); }

        /* ---- inline editor (replaces detail body while composing) ---- */
        .pnc-editor-wrap{ padding:22px 24px; overflow-y:auto; flex:1; min-height:0; scroll-behavior:smooth; scrollbar-width:thin; }
        .pnc-editor-label{ font-size:11.5px; text-transform:uppercase; letter-spacing:.04em; font-weight:700; color:var(--pnc-muted); margin-bottom:10px; }
        .pnc-editor-actions{ display:flex; align-items:center; gap:10px; justify-content:flex-end; margin-top:14px; }
        .pnc-loading{ flex:1; display:flex; align-items:center; justify-content:center; }
      `}</style>

      {/* -------- Top Bar -------- */}
      <div className="pnc-topbar">
        <div>
          <p className="pnc-title">Notes</p>
          <p className="pnc-subtitle">Capture and track notes linked to this project</p>
        </div>
        <div className="pnc-topbar-actions">
          <div className="pnc-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            className="pnc-btn-primary"
            onClick={openCreateNote}
            disabled={
              isNotesLoading || projectData?.project_status === "complete"
            }
          >
            {isNotesLoading && isNotesOpen && !editingNoteId ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlus} /> Add
              </>
            )}
          </button>
        </div>
      </div>

      {!noteGrant?.isView ? (
        <div className="pnc-body">
          <UnauthViewModal tabName={"Notes"} />
        </div>
      ) : (
        <div className="pnc-body">
          {/* -------- Left: Notes list -------- */}
          <div className="pnc-list-panel">
            <div className="pnc-card pnc-list-card">
              <div className="pnc-list-card-head">
                <h6>
                  <FaStickyNote color="var(--pnc-primary)" />
                  Notes
                </h6>
                <span className="pnc-count-pill">{filteredNotes.length}</span>
              </div>

              {isInitialLoading ? (
                <div className="pnc-loading">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading notes...</span>
                  </div>
                </div>
              ) : filteredNotes.length > 0 ? (
                <div className="pnc-list">
                  {filteredNotes.map((note) => {
                    const created = new Date(note.created_at);
                    const dateLabel =
                      !isNaN(created) && isSameDay(created, now)
                        ? "Today"
                        : formatDateInAlphabeticalMonth(note.created_at);
                    return (
                      <div
                        key={note.id}
                        className={`pnc-item ${selectedNoteId === note.id ? "active" : ""}`}
                        onClick={() => {
                          setIsNotesOpen(false);
                          setEditingNoteId(null);
                          setSelectedNoteId(note.id);
                        }}
                      >
                        <div className="pnc-item-avatar">{initialsOf(note.added_name)}</div>
                        <div className="pnc-item-content">
                          <div className="pnc-item-snippet">
                            {truncate(stripHtml(note.notes), 90) || "Untitled note"}
                          </div>
                          <div className="pnc-item-meta">
                            <span>{note.added_name}</span>
                            <span className="dot" />
                            <span>{dateLabel}</span>
                            <span className="dot" />
                            <span>{formatTime(note.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="pnc-list-empty">
                  {searchTerm ? "No notes match your search." : "This project doesn't have any notes yet."}
                  <div className="mt-2">
                    <button
                      className="pnc-btn-primary"
                      style={{ margin: "10px auto 0" }}
                      onClick={openCreateNote}
                      disabled={projectData?.project_status === "complete"}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add Note
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* -------- Right: Note Details / Editor -------- */}
          <div className="pnc-detail-panel">
            <div className="pnc-detail-card">
              {isNotesOpen ? (
                <>
                  <div className="pnc-detail-header">
                    <h6>{editingNoteId ? "Edit Note" : "New Note"}</h6>
                  </div>
                  <div className="pnc-editor-wrap" ref={notesRef}>
                    <div className="pnc-editor-label">Note content</div>
                    <ReactQuill
                      theme="snow"
                      value={notesContent}
                      onChange={(value) => setNotesContent(value)}
                      placeholder="Write your message..."
                      style={{ minHeight: "220px" }}
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, false] }],
                          ["bold", "italic", "underline", "link"],
                          [{ list: "ordered" }, { list: "bullet" }],
                          ["blockquote", "code-block"],
                          ["clean"],
                        ],
                      }}
                    />
                    {notesError && (
                      <p className="text-danger mt-2 mb-0" style={{ fontSize: "12px" }}>
                        {notesError}
                      </p>
                    )}
                    <div className="pnc-editor-actions">
                      <button
                        onClick={closeEditor}
                        disabled={isNotesLoading}
                        className="btn btn-default-outline"
                        style={styles.listButtonsStyles}
                      >
                        Cancel
                      </button>
                      <button
                        className="pnc-btn-primary"
                        onClick={handleSaveNote}
                        style={{ ...styles.listButtonsStyles, border: "none" }}
                        disabled={isNotesLoading}
                      >
                        {isNotesLoading
                          ? "Saving..."
                          : editingNoteId
                            ? "Update"
                            : "Save"}
                      </button>
                    </div>
                  </div>
                </>
              ) : !selectedNote ? (
                <div className="pnc-detail-empty">
                  <div className="pnc-detail-empty-icon">
                    <FaStickyNote />
                  </div>
                  <p className="mb-0">Select a note to view its details</p>
                </div>
              ) : (
                <>
                  <div className="pnc-detail-header">
                    <h6>Note Details</h6>
                    <Dropdown className="pnc-card-actions" align="end">
                      <Dropdown.Toggle
                        variant="light"
                        className="pnc-no-arrow p-0 m-0 bg-transparent border-0"
                      >
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="z-3">
                        <Dropdown.Item
                          disabled={projectData?.project_status === "complete"}
                          onClick={() => {
                            if (!noteGrant?.isEdit) {
                              toast.warning("You are not authorized for this action!");
                              return;
                            }
                            handleEditNote(selectedNote.id);
                          }}
                        >
                          Edit
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            if (!noteGrant?.isDelete) {
                              toast.warning("You are not authorized for this action!");
                              return;
                            }
                            setNoteToDelete(selectedNote);
                            setShowDeleteModal(true);
                          }}
                        >
                          Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  <div className="pnc-detail-body">
                    <div className="pnc-detail-top">
                      <div className="pnc-detail-avatar">{initialsOf(selectedNote.added_name)}</div>
                      <div>
                        <div className="pnc-detail-name">{selectedNote.added_name}</div>
                        <div className="pnc-detail-date">
                          <FontAwesomeIcon icon={faCalendarDays} style={{ color: "var(--pnc-primary)" }} />
                          {formatDateInAlphabeticalMonth(selectedNote.created_at)}
                          <FontAwesomeIcon icon={faClock} style={{ color: "var(--pnc-primary)", marginLeft: "6px" }} />
                          {formatTime(selectedNote.created_at)}
                        </div>
                      </div>
                      <span
                        className="pnc-status-pill"
                        style={{
                          background:
                            selectedNote.status?.toLowerCase() === "active" ? "#17c185" : "#8a8fa3",
                          color: "#fff",
                        }}
                      >
                        {selectedNote.status || "Active"}
                      </span>
                    </div>

                    <div
                      className="pnc-detail-content"
                      dangerouslySetInnerHTML={{ __html: selectedNote.notes }}
                    />

                    {selectedNote.updated_at && (
                      <div className="pnc-detail-updated">
                        Last updated {formatDateInAlphabeticalMonth(selectedNote.updated_at)} at{" "}
                        {formatTime(selectedNote.updated_at)}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => handleDeleteNote(noteToDelete)}
        note={noteToDelete}
        isLoading={isNotesLoading}
        message="Are you want delete notes?"
      />
    </div>
  );
};

export default ProjectNotes;